import { useState } from "react";
import { Link } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import { useCart } from "../../hooks/useCart";
import { useToastContext } from "../../context/ToastContext";
import { formatPrice } from "../../utils/formatPrice";
import { saveOrder } from "../../utils/localStorage";
import styles from "./CheckoutPage.module.css";

const INITIAL_FORM = {
  name: "",
  phone: "",
  email: "",
  address: "",
  payment: "credit",
  note: "",
};

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "請輸入收件人姓名";
  if (!form.phone.trim()) {
    errors.phone = "請輸入電話";
  } else if (!/^[0-9\-+\s]{8,15}$/.test(form.phone)) {
    errors.phone = "電話格式不正確";
  }
  if (!form.email.trim()) {
    errors.email = "請輸入 Email";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Email 格式不正確";
  }
  if (!form.address.trim()) errors.address = "請輸入地址";
  return errors;
}

function generateOrderId() {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, "");
  const rand = String(Math.floor(Math.random() * 1000)).padStart(3, "0");
  return `ORD-${date}-${rand}`;
}

function CheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const { addToast } = useToastContext();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [order, setOrder] = useState(null);

  if (items.length === 0 && !order) {
    return (
      <div className={styles.empty}>
        <p>購物車是空的，請先加入商品。</p>
        <Link to="/products" className={styles.backLink}>去選購</Link>
      </div>
    );
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: undefined }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const newOrder = {
      id: generateOrderId(),
      status: "processing",
      items: items.map(item => ({
        ...item,
        unitPrice: item.salePrice !== null ? item.salePrice : item.price,
      })),
      total: totalPrice,
      createdAt: new Date().toISOString(),
      shippingInfo: {
        name: form.name,
        phone: form.phone,
        email: form.email,
        address: form.address,
        payment: form.payment,
        note: form.note,
      },
    };

    saveOrder(newOrder);
    clearCart();
    setOrder(newOrder);
    addToast("訂單已送出！", "success");
  }

  if (order) {
    return (
      <div className={styles.success}>
        <FaCheckCircle className={styles.successIcon} />
        <h2 className={styles.successTitle}>訂單已成立！</h2>
        <p className={styles.successOrderId}>訂單編號：{order.id}</p>

        <div className={styles.orderSummary}>
          <h3>商品明細</h3>
          {order.items.map(item => (
            <div key={item.productId} className={styles.orderItem}>
              <img src={item.image} alt={item.name} className={styles.orderItemImg} />
              <div className={styles.orderItemInfo}>
                <p className={styles.orderItemName}>{item.name}</p>
                <p className={styles.orderItemQty}>×{item.quantity}</p>
              </div>
              <span className={styles.orderItemTotal}>
                {formatPrice(item.unitPrice * item.quantity)}
              </span>
            </div>
          ))}
          <div className={styles.orderTotal}>
            <span>合計</span>
            <span>{formatPrice(order.total)}</span>
          </div>
        </div>

        <div className={styles.successActions}>
          <Link to="/orders" className={styles.ordersBtn}>查看訂單紀錄</Link>
          <Link to="/products" className={styles.continueBtn}>繼續購物</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>結帳</h1>

        <div className={styles.layout}>
          {/* Form */}
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <h3 className={styles.sectionTitle}>收件資訊</h3>

            <div className={styles.field}>
              <label htmlFor="name" className={styles.label}>收件人姓名 *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
                placeholder="王小明"
              />
              {errors.name && <p className={styles.error}>{errors.name}</p>}
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor="phone" className={styles.label}>電話 *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  className={`${styles.input} ${errors.phone ? styles.inputError : ""}`}
                  placeholder="0912-345-678"
                />
                {errors.phone && <p className={styles.error}>{errors.phone}</p>}
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
                  placeholder="example@email.com"
                />
                {errors.email && <p className={styles.error}>{errors.email}</p>}
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="address" className={styles.label}>地址 *</label>
              <input
                id="address"
                name="address"
                type="text"
                value={form.address}
                onChange={handleChange}
                className={`${styles.input} ${errors.address ? styles.inputError : ""}`}
                placeholder="台北市信義區信義路一段 1 號"
              />
              {errors.address && <p className={styles.error}>{errors.address}</p>}
            </div>

            <div className={styles.field}>
              <label className={styles.label}>付款方式</label>
              <div className={styles.paymentOptions}>
                <label className={`${styles.paymentOpt} ${form.payment === "credit" ? styles.paymentOptActive : ""}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="credit"
                    checked={form.payment === "credit"}
                    onChange={handleChange}
                  />
                  信用卡
                </label>
                <label className={`${styles.paymentOpt} ${form.payment === "cod" ? styles.paymentOptActive : ""}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="cod"
                    checked={form.payment === "cod"}
                    onChange={handleChange}
                  />
                  貨到付款
                </label>
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="note" className={styles.label}>備註（選填）</label>
              <textarea
                id="note"
                name="note"
                value={form.note}
                onChange={handleChange}
                className={styles.textarea}
                placeholder="特殊需求或配送說明..."
                rows={3}
              />
            </div>

            <button type="submit" className={styles.submitBtn}>
              確認下單
            </button>
          </form>

          {/* Order Preview */}
          <div className={styles.preview}>
            <h3 className={styles.sectionTitle}>訂單確認</h3>
            {items.map(item => {
              const unitPrice = item.salePrice !== null ? item.salePrice : item.price;
              return (
                <div key={item.productId} className={styles.previewItem}>
                  <img src={item.image} alt={item.name} className={styles.previewImg} />
                  <div className={styles.previewInfo}>
                    <p className={styles.previewName}>{item.name}</p>
                    <p className={styles.previewQty}>×{item.quantity}</p>
                  </div>
                  <span className={styles.previewTotal}>{formatPrice(unitPrice * item.quantity)}</span>
                </div>
              );
            })}
            <div className={styles.previewTotal}>
              <span>運費</span>
              <span className={styles.free}>免費</span>
            </div>
            <div className={`${styles.previewTotal} ${styles.previewGrandTotal}`}>
              <span>合計</span>
              <span>{formatPrice(totalPrice)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
